import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import type { CardBrandBadge as BrandBadge } from "@/types/home-triage"

/**
 * OKS-708 — Per-card brand badge for the agency cross-brand triage queue.
 *
 * A mixed-brand section (candidates / submissions aggregated across the
 * agency's assigned brands) must always attribute each card to its owning
 * brand. Rendered at the card's top-left by the shared
 * `CandidateReviewCard` / `SubmissionReviewCard` ONLY when the item carries
 * a `brand` (the single-brand OKS-706/707 consumers omit it → no badge).
 *
 * Identity is conveyed by TEXT (brand name), never colour alone (UX Spec
 * §6). The logo renders when available; otherwise the brand-name initial is
 * the fallback avatar. Non-interactive (identity only, not a filter
 * shortcut in v1).
 */
export interface CardBrandBadgeProps {
  brand: BrandBadge
  className?: string
}

export function CardBrandBadge({ brand, className }: CardBrandBadgeProps) {
  return (
    <Badge
      variant="outline"
      data-testid="card-brand-badge"
      className={cn("w-fit gap-1.5 py-0.5 pr-2 pl-1", className)}
    >
      {brand.logo_url ? (
        <img
          src={brand.logo_url}
          alt=""
          aria-hidden="true"
          className="size-4 rounded-full object-cover"
        />
      ) : (
        <span
          aria-hidden="true"
          className="bg-muted text-muted-foreground flex size-4 items-center justify-center rounded-full text-[10px] font-semibold uppercase"
        >
          {brand.name.trim().charAt(0)}
        </span>
      )}
      <span className="max-w-[12rem] truncate">{brand.name}</span>
    </Badge>
  )
}
